import { defineStore } from "pinia";

import { useApi } from "@/composables/useApi";
import type { IAuthTokens, IAuthUser } from "@/types/auth";
import type { IFetchOptions } from "@/types/common";

interface IState {
  tokens: {
    access?: string;
    refresh?: string;
  };
  user?: IAuthUser;
  preloader: boolean;
  phone?: string;
  session?: string;
}

export const useAuthStore = defineStore("auth", {
  state: (): IState => ({
    tokens: {},
    user: undefined,
    preloader: false,
    phone: localStorage.getItem("phone") || "",
    session: localStorage.getItem("session") || "",
  }),
  getters: {
    isAuthenticated: (state) =>
      !!state.tokens?.access || !!state.tokens?.refresh,
    getTokens: (state) => state.tokens,
    hasUser: (state) => Object.keys(state.user || {}).length > 0,
    authUser: (state) => state.user,
  },
  actions: {
    /**
     * Phone + password against admission-usat-api. The same endpoint serves enrolled students
     * (matched on their contract's usat_password) and applicants (matched on their member
     * password), so the app does not need to know which it is talking to.
     */
    login(data: { phone: string; password: string }, isRemember = false) {
      return new Promise((resolve, reject) => {
        useApi()
          .$post<IAuthTokens>("v2/users/login/", data)
          .then((response) => {
            this.setTokens(response, isRemember);
            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    initTokens() {
      const isRemember = localStorage.getItem("remember_me") === "true";
      const access = isRemember
        ? localStorage.getItem("access_token")
        : sessionStorage.getItem("access_token");
      const refresh = isRemember
        ? localStorage.getItem("refresh_token")
        : sessionStorage.getItem("refresh_token");
      if (access) this.tokens.access = access;
      if (refresh) this.tokens.refresh = refresh;
    },

    logout() {
      this.tokens = {};
      this.user = undefined;
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("access_token");
    },

    setTokens(tokens: Partial<IAuthTokens>, isRemember = false) {
      if (isRemember) {
        localStorage.setItem("remember_me", "true");
      } else {
        localStorage.removeItem("remember_me");
      }
      if (tokens?.access) {
        this.tokens.access = tokens.access;
        if (isRemember) {
          localStorage.setItem("access_token", tokens.access);
        } else {
          sessionStorage.setItem("access_token", tokens.access);
        }
      }
      if (tokens?.refresh) {
        this.tokens.refresh = tokens.refresh;
        if (isRemember) {
          localStorage.setItem("refresh_token", tokens.refresh);
        } else {
          sessionStorage.setItem("refresh_token", tokens.refresh);
        }
      }
    },

    getProfile(options?: Pick<IFetchOptions, "force">) {
      return new Promise((resolve, reject) => {
        if (!options?.force && this.hasUser) {
          resolve(this.user);
          return;
        }
        useApi()
          .$get<IAuthUser>("v2/users/get-profile/")
          .then((data) => {
            this.user = data;
            resolve(data);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    hasAccessToken() {
      const isRemember = localStorage.getItem("remember_me") === "true";
      const authToken = isRemember
        ? localStorage.getItem("access_token")
        : sessionStorage.getItem("access_token");
      return !!authToken;
    },
  },
});
